/**
 * User Overrides Panel Component
 */

import { useState } from 'react';
import { Plus, Trash2, AlertCircle, User } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { useI18n } from '@/i18n';
import {
  useUserOverrides,
  useSetUserOverride,
  useDeleteUserOverride,
  useDeleteAllUserOverrides,
} from '../hooks/useFeatureFlags';

interface UserOverridesPanelProps {
  flagNames: string[];
}

export function UserOverridesPanel({ flagNames }: UserOverridesPanelProps) {
  const { t } = useI18n();
  const { data, isLoading, error } = useUserOverrides();
  const setOverrideMutation = useSetUserOverride();
  const deleteOverrideMutation = useDeleteUserOverride();
  const deleteAllMutation = useDeleteAllUserOverrides();

  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newUserId, setNewUserId] = useState('');
  const [newFlagName, setNewFlagName] = useState('');
  const [newEnabled, setNewEnabled] = useState(true);
  const [formError, setFormError] = useState('');

  const overrides = data?.user_overrides ?? {};
  const userIds = Object.keys(overrides);

  const handleAddOverride = async () => {
    if (!newUserId.trim()) {
      setFormError(t('featureFlags.userOverrides.userIdRequired'));
      return;
    }
    if (!newFlagName) {
      setFormError(t('featureFlags.userOverrides.selectFlagRequired'));
      return;
    }

    try {
      await setOverrideMutation.mutateAsync({
        user_id: newUserId.trim(),
        flag_name: newFlagName,
        enabled: newEnabled,
      });
      setShowAddDialog(false);
      setNewUserId('');
      setNewFlagName('');
      setNewEnabled(true);
      setFormError('');
    } catch {
      setFormError(t('featureFlags.userOverrides.addFailed'));
    }
  };

  const handleDeleteOverride = async (userId: string, flagName: string) => {
    try {
      await deleteOverrideMutation.mutateAsync({ userId, flagName });
    } catch {
      // Error handled by React Query
    }
  };

  const handleDeleteAllForUser = async (userId: string) => {
    if (confirm(t('featureFlags.userOverrides.clearAllConfirm', { userId }))) {
      try {
        await deleteAllMutation.mutateAsync({ userId });
      } catch {
        // Error handled by React Query
      }
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
          <div>
            <h3 className="text-red-800 font-semibold">{t('featureFlags.userOverrides.errorLoading')}</h3>
            <p className="text-red-600 text-sm">{(error as Error).message}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-medium text-gray-900">{t('featureFlags.userOverrides.title')}</h3>
          <p className="text-sm text-gray-500">
            {t('featureFlags.userOverrides.description')}
          </p>
        </div>
        <Button variant="primary" onClick={() => setShowAddDialog(true)}>
          <Plus className="w-4 h-4 mr-2" />
          {t('featureFlags.userOverrides.addOverride')}
        </Button>
      </div>

      {/* Overrides List */}
      {userIds.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <User className="w-12 h-12 mx-auto mb-2 opacity-50" />
          <p>{t('featureFlags.userOverrides.noOverrides')}</p>
          <p className="text-sm mt-1">
            {t('featureFlags.userOverrides.noOverridesHint')}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {userIds.map((userId) => (
            <div
              key={userId}
              className="border border-gray-200 rounded-lg overflow-hidden"
            >
              {/* User Header */}
              <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200">
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-gray-500" />
                  <span className="font-mono text-sm font-medium">{userId}</span>
                  <span className="text-xs text-gray-500">
                    ({Object.keys(overrides[userId]).length} {t('featureFlags.userOverrides.override')}
                    {Object.keys(overrides[userId]).length !== 1 ? 's' : ''})
                  </span>
                </div>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => handleDeleteAllForUser(userId)}
                  loading={deleteAllMutation.isPending}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>

              {/* User Flags */}
              <div className="divide-y divide-gray-100">
                {Object.entries(overrides[userId]).map(([flagName, enabled]) => (
                  <div
                    key={flagName}
                    className="flex items-center justify-between px-4 py-2"
                  >
                    <div className="flex items-center gap-3">
                      <span
                        className={`
                          w-2 h-2 rounded-full
                          ${enabled ? 'bg-green-500' : 'bg-red-500'}
                        `}
                      />
                      <span className="font-mono text-sm">{flagName}</span>
                      <span
                        className={`
                          text-xs px-2 py-0.5 rounded
                          ${
                            enabled
                              ? 'bg-green-100 text-green-700'
                              : 'bg-red-100 text-red-700'
                          }
                        `}
                      >
                        {enabled ? t('featureFlags.userOverrides.enabled').toLowerCase() : t('featureFlags.userOverrides.disabled').toLowerCase()}
                      </span>
                    </div>
                    <button
                      onClick={() => handleDeleteOverride(userId, flagName)}
                      className="p-1 text-gray-400 hover:text-red-600 rounded"
                      disabled={deleteOverrideMutation.isPending}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Override Dialog */}
      {showAddDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setShowAddDialog(false)}
          />
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {t('featureFlags.userOverrides.addTitle')}
            </h3>

            <div className="space-y-4">
              {/* User ID */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('featureFlags.userOverrides.userId')}
                </label>
                <input
                  type="text"
                  value={newUserId}
                  onChange={(e) => setNewUserId(e.target.value)}
                  placeholder={t('featureFlags.userOverrides.userIdPlaceholder')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>

              {/* Flag Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('featureFlags.userOverrides.flag')}
                </label>
                <select
                  value={newFlagName}
                  onChange={(e) => setNewFlagName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">{t('featureFlags.userOverrides.selectFlagPlaceholder')}</option>
                  {flagNames.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Enabled Toggle */}
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-gray-700">
                  {t('featureFlags.userOverrides.overrideValue')}
                </label>
                <div className="flex items-center gap-2">
                  <span
                    className={`text-sm ${!newEnabled ? 'font-medium' : 'text-gray-500'}`}
                  >
                    {t('featureFlags.userOverrides.disabled')}
                  </span>
                  <button
                    type="button"
                    onClick={() => setNewEnabled(!newEnabled)}
                    className={`
                      relative w-11 h-6 rounded-full transition-colors
                      ${newEnabled ? 'bg-green-500' : 'bg-gray-300'}
                    `}
                  >
                    <span
                      className={`
                        absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow
                        transition-transform
                        ${newEnabled ? 'translate-x-5' : 'translate-x-0'}
                      `}
                    />
                  </button>
                  <span
                    className={`text-sm ${newEnabled ? 'font-medium' : 'text-gray-500'}`}
                  >
                    {t('featureFlags.userOverrides.enabled')}
                  </span>
                </div>
              </div>

              {formError && (
                <p className="text-sm text-red-600">{formError}</p>
              )}
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowAddDialog(false);
                  setFormError('');
                }}
              >
                {t('common.cancel')}
              </Button>
              <Button
                variant="primary"
                onClick={handleAddOverride}
                loading={setOverrideMutation.isPending}
              >
                {t('featureFlags.userOverrides.addOverride')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
